import React from 'react';
import { Minus, Plus } from 'lucide-react';
import { TouchRangeInput } from './TouchRangeInput';

const deFmt = (v) => Number(v).toLocaleString('de-DE');

/**
 * RangeSliderField — intuitiver Slider mit:
 *  · großer Ist-Zahl oben rechts
 *  · Wert-Bubble direkt am Thumb (zeigt die EXAKTE Zahl beim Ziehen — Desktop & Mobile)
 *  · optionalen −/+ Schrittknöpfen für präzise Eingabe (stepBy)
 *  · Min/Max-Labels
 * onValueChange(number) setzt den Wert (bereits geclamped).
 */
export const RangeSliderField = ({
  label, hint, value, min, max, step = 1, unit = '',
  displayValue, onValueChange, stepBy, minLabel, maxLabel,
  testId, dark = false,
}) => {
  const nMin = Number(min);
  const nMax = Number(max);
  const nVal = Math.min(Math.max(Number(value), nMin), nMax);
  const pct = ((nVal - nMin) / (nMax - nMin || 1)) * 100;
  const shown = displayValue != null ? displayValue : `${deFmt(nVal)}${unit ? ` ${unit}` : ''}`;
  const inc = stepBy || Number(step) || 1;
  const setClamped = (v) => onValueChange(Math.min(Math.max(v, nMin), nMax));

  const labelColor = dark ? 'text-white/60' : 'text-stone-500';
  const valueColor = dark ? 'text-white' : 'text-stone-900';
  const minmaxColor = dark ? 'text-white/40' : 'text-stone-400';
  const btnCls = dark
    ? 'border-white/15 text-white/70 hover:text-white hover:border-white/40'
    : 'border-stone-200 text-stone-500 hover:text-stone-900 hover:border-stone-900';

  return (
    <div data-testid={testId ? `${testId}-field` : undefined}>
      <div className="flex items-baseline justify-between gap-3 mb-2.5">
        <div className="min-w-0">
          <span className={`text-[11px] leading-tight block ${labelColor}`}>{label}</span>
          {hint && <span className={`text-[10px] ${minmaxColor}`}>{hint}</span>}
        </div>
        <span
          className={`text-lg sm:text-xl font-display tabular-nums leading-none flex-shrink-0 ${valueColor}`}
          data-testid={testId ? `${testId}-value` : undefined}
        >
          {shown}
        </span>
      </div>

      <div className="flex items-center gap-2.5">
        {stepBy != null && (
          <button
            type="button"
            onClick={() => setClamped(nVal - inc)}
            disabled={nVal <= nMin}
            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed ${btnCls}`}
            data-testid={testId ? `${testId}-minus` : undefined}
            aria-label={`${label} verringern`}
          >
            <Minus className="w-4 h-4" strokeWidth={2.2} />
          </button>
        )}

        <div className="relative flex-1 pt-8">
          {/* Wert-Bubble exakt am Thumb (Formel kompensiert die Thumb-Breite) */}
          <div
            className="absolute top-0 pointer-events-none select-none z-10 transition-[left] duration-75 ease-out"
            style={{ left: `calc(${pct}% + ${(0.5 - pct / 100) * 28}px)`, transform: 'translateX(-50%)' }}
            aria-hidden="true"
          >
            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-500 text-white text-[11px] font-bold tabular-nums whitespace-nowrap shadow-[0_2px_10px_-1px_rgba(16,244,98,0.65)]">
              {shown}
            </span>
          </div>

          <TouchRangeInput
            min={min}
            max={max}
            step={step}
            value={nVal}
            onChange={(e) => onValueChange(parseInt(e.target.value, 10))}
            className={`fuelx-slider ${dark ? 'fuelx-slider-dark' : 'fuelx-slider-light'} w-full`}
            aria-label={label}
            data-testid={testId ? `${testId}-slider` : undefined}
          />
        </div>

        {stepBy != null && (
          <button
            type="button"
            onClick={() => setClamped(nVal + inc)}
            disabled={nVal >= nMax}
            className={`w-9 h-9 rounded-full border flex items-center justify-center transition-colors flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed ${btnCls}`}
            data-testid={testId ? `${testId}-plus` : undefined}
            aria-label={`${label} erhöhen`}
          >
            <Plus className="w-4 h-4" strokeWidth={2.2} />
          </button>
        )}
      </div>

      {(minLabel || maxLabel) && (
        <div className={`flex justify-between text-[10px] mt-1.5 ${minmaxColor}`}>
          <span>{minLabel}</span><span>{maxLabel}</span>
        </div>
      )}
    </div>
  );
};

export default RangeSliderField;
